import { classNames } from "../utils/classNames";
import { Icon } from "./Icon";
import { ChangeEvent, ChangeEventHandler, ReactElement, useContext } from "react";
import { Box } from "./Box";
import { observer } from "mobx-react";
import { Alert } from "./Alert";

import { useDispatch, useTranslation } from "../stores/RootStoreContext";

import { DCEvent } from "../events/eventsDomain";
import { OptionFieldModel } from "../common/commonForms";
import { TableContext } from "./TableContext";
import { isTMessage, TMessage } from "../i18n/tt";

export type CheckboxProps = {
  name?: string;
  label?: ReactElement | string | number | TMessage;
  checked?: boolean;
  disabled?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  value?: string;
  id?: string;
};

export const Checkbox = observer(
  ({ name, label, checked, disabled, onChange, value, id }: CheckboxProps) => {
    const tableContext = useContext(TableContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const checkbox = (
      <div className={"tw-flex tw-relative tw-w-5 tw-leading-6 tw-flex-none"}>
        <input
          type="checkbox"
          className={classNames(
            "tw-top-0.5",
            "tw-absolute",
            "tw-appearance-none",
            "tw-w-5",
            "tw-h-5",
            "tw-border-2",
            "tw-rounded",
            disabled ? "tw-cursor-default" : "tw-cursor-pointer",
            disabled && checked && ["border-gray", "bg-gray"],
            disabled && !checked && ["border-gray", "bg-default"],
            !disabled &&
              checked && [
                "border-primary",
                "bg-primary",
                "hover:border-primary-dark",
                "hover:bg-primary-dark",
              ],
            !disabled &&
              !checked && [
                "bg-border-gray-hard",
                "bg-default",
                "hover:border-primary",
                "hover:bg-primary-light",
              ],
          )}
          name={name}
          checked={checked}
          onChange={(event) => {
            const result = onChange?.(event);
            if (Array.isArray(result)) {
              dispatch(result);
            }
          }}
          disabled={disabled}
          value={value}
          id={id}
        />
        <div
          className={classNames(
            "tw-top-0.5",
            "tw-absolute",
            "tw-w-5",
            "tw-h-5",
            "text-lg",
            "tw-justify-center",
            "tw-items-center",
            "color-primary-contrast",
            "tw-pointer-events-none",
            checked ? "tw-flex" : ["tw-hidden"],
          )}
        >
          <Icon name={"check"} weight={"bold"} />
        </div>
      </div>
    );

    if (label) {
      return (
        <label
          className={classNames(
            "tw-flex",
            "tw-gap-2",
            "tw-items-start",
            disabled ? ["tw-cursor-default", "subtle"] : ["tw-cursor-pointer", "color-default"],
          )}
        >
          {checkbox}
          <div className={"tw-leading-6"}>{isTMessage(label) ? t(label) : label}</div>
        </label>
      );
    }

    if (tableContext) {
      return (
        <div className="tw-flex">
          {checkbox}
          &nbsp;
        </div>
      );
    }
    return checkbox;
  },
);

export type CheckboxGroupProps<T> = {
  name?: string;
  options: T[];
  idFn: (item: T) => string;
  labelFn: (item: T) => string;
  checkedFn: (item: T) => boolean;
  disabledFn?: (item: T) => boolean;
  disabled?: boolean;
  horizontal?: boolean;
  onChange?: (
    event: ChangeEvent<HTMLInputElement>,
    option: T,
    checked: boolean,
  ) => void | DCEvent[];
};

export function CheckboxGroup<T>({
  name,
  horizontal,
  options,
  idFn,
  labelFn,
  checkedFn,
  disabled,
  onChange,
}: CheckboxGroupProps<T>) {
  const dispatch = useDispatch();
  return (
    <Box vertical={!horizontal} gap={horizontal ? "md" : "sm"}>
      {options.map((option) => (
        <Checkbox
          key={idFn(option)}
          name={name}
          disabled={disabled}
          label={labelFn(option)}
          onChange={(event) => {
            const result = onChange?.(event, option, event.target.checked);
            if (Array.isArray(result)) {
              dispatch(result);
            }
          }}
          checked={checkedFn(option)}
        />
      ))}
    </Box>
  );
}

export const MCheckboxGroup = observer(function MCheckboxGroup<T>({
  model,
  name,
  options,
  idFn,
  labelFn,
  checkedFn,
  disabledFn,
  disabled,
  horizontal,
  onChange,
}: {
  model: OptionFieldModel<T>;
} & Partial<CheckboxGroupProps<T>>) {
  const { t } = useTranslation();
  return (
    <Box vertical={!horizontal} gap={horizontal ? "md" : "sm"}>
      {model.error !== undefined && <Alert type={"error"} message={<>{t(model.error)}</>} />}
      {(options ?? model.options).map((option) => (
        <Checkbox
          key={(idFn ?? model.getId)(option)}
          name={name}
          disabled={disabled || model.isDisabled(option) || disabledFn?.(option)}
          label={labelFn?.(option) ?? model.getLabel(option)}
          onChange={(event) => {
            if (onChange) {
              onChange(event, option, event.target.checked);
            } else {
              model.toggle(option);
            }
          }}
          checked={checkedFn?.(option) ?? model.isSelected(option)}
        />
      ))}
    </Box>
  );
});
